import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { Download, FileText, Search } from "lucide-react";
import { listResources, resourceDownloadUrl } from "../lib/api";

const CATEGORIES = [
  "All",
  "Parent Guides",
  "Youth Resources",
  "Program Brochures",
  "Mental Wellness",
  "Drug Prevention",
  "School Partnerships",
  "Sponsorship Packets",
];

export default function ResourceLibrary() {
  const [params, setParams] = useSearchParams();
  const initialCat = params.get("category") || "All";
  const [cat, setCat] = useState(initialCat);
  const [search, setSearch] = useState("");

  const { data: resources = [], isLoading } = useQuery({
    queryKey: ["public-resources", cat],
    queryFn: () => listResources(cat === "All" ? null : cat),
  });

  const filtered = resources.filter((r) =>
    search
      ? (r.title + " " + (r.description || "")).toLowerCase().includes(search.toLowerCase())
      : true
  );

  const setCategory = (c) => {
    setCat(c);
    if (c === "All") setParams({}); else setParams({ category: c });
  };

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0A0A0A] text-white" data-testid="resources-hero">
        <div className="noise" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#6B21A8]/35 via-transparent to-transparent" />
        <div className="container-x relative py-20 md:py-24">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="gold-line" />
              <span className="overline text-[#F5B90A]">Resource Library</span>
            </div>
            <h1 className="font-display text-5xl md:text-6xl font-bold leading-[1.02]">
              Downloadable guides for <span className="italic text-[#F5B90A]">families, youth, and partners.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-lg text-white/80">
              Browse approved resources organized by audience and topic. New materials are added regularly.
            </p>
          </div>
        </div>
      </section>

      {/* FILTERS */}
      <section className="bg-[#F7F3EC] py-10 border-y border-black/5" data-testid="resources-filters">
        <div className="container-x">
          <div className="flex flex-wrap gap-2 mb-5">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                data-testid={`resource-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
                className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${cat === c ? "bg-[#6B21A8] text-white" : "bg-white text-black/80 hover:bg-white/80 border border-black/10"}`}
              >
                {c}
              </button>
            ))}
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-black/40" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search resources..."
              className="w-full md:w-96 rounded-md border border-black/10 bg-white pl-10 pr-3 py-2 text-sm"
              data-testid="resource-search"
            />
          </div>
        </div>
      </section>

      {/* LIST */}
      <section className="bg-white py-16" data-testid="resources-list">
        <div className="container-x">
          {isLoading ? (
            <p className="text-black/60">Loading resources…</p>
          ) : filtered.length === 0 ? (
            <div className="rounded-lg border border-dashed border-black/10 p-14 text-center">
              <FileText className="h-10 w-10 mx-auto text-black/30" />
              <p className="mt-4 font-display text-xl">No resources published yet{cat !== "All" ? ` in "${cat}"` : ""}.</p>
              <p className="mt-2 text-sm text-black/60">New resources will appear here once approved and uploaded by an administrator.</p>
            </div>
          ) : (
            <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
              {filtered.map((r) => (
                <a key={r.id} href={resourceDownloadUrl(r.id)} target="_blank" rel="noreferrer" className="card-editorial p-6 block group" data-testid={`resource-${r.id}`}>
                  <p className="overline text-[#6B21A8]">{r.category}</p>
                  <h4 className="mt-2 font-display text-lg font-bold group-hover:text-[#6B21A8]">{r.title}</h4>
                  {r.description && <p className="mt-2 text-sm text-black/70">{r.description}</p>}
                  <p className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-black/80">
                    <Download className="h-4 w-4" /> Download PDF
                  </p>
                </a>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
